// context/api-keys.context.js
import { createContext } from 'preact';
import { html } from '@shared/utils/rendering/html';
import { useCallback, useContext, useEffect, useMemo, useState } from 'preact/hooks';

import { useApiKeysData } from '../hooks/use-api-keys-data.js';
import { useApiKeysMutations } from '../hooks/use-api-key-mutations.js';
import { useSortAndPage } from '../hooks/use-sort-and-page.js';
import {
    ApiKeysFilters,
    ApiKeysColumns,
} from '@core/configs/admin/systems/api-keys-table/api-keys-table.config';
import ApiKeysActions from '../api-keys-table.actions.js';

const ApiKeysTableContext = createContext();

export default function ApiKeysTableProvider({ children }) {
    const [remoteSystemId, setRemoteSystemId] = useState(null);

    // filters state (sorting + pagination only used on client)
    const [localFilters, setLocalFilters] = useState(() => ({
        ...ApiKeysFilters,
        sorting: { ...(ApiKeysFilters?.sorting || {}) },
        pagination: { ...(ApiKeysFilters?.pagination || {}) },
    }));

    const [paginationData, setPaginationData] = useState({
        pageNumber: 1,
        pageSize: 10,
    });

    // data (no filters sent; full list returned)
    const { data: apiKeys, isLoading, fetchCacheKey } = useApiKeysData(remoteSystemId);

    // mutations (handles cache updates)
    const {
        isSubmitting,
        invalidateKeyMutation,
        activateKeyMutation,
        extendKeyMutation,
        generateKeyMutation,
    } = useApiKeysMutations(fetchCacheKey);

    // client-side sort + paging
    const { rows, total } = useSortAndPage(apiKeys || [], localFilters.sorting, paginationData);

    // keep pageNumber in range when data size changes
    useEffect(() => {
        setPaginationData((prev) => {
            const maxPage = Math.max(1, Math.ceil(total / prev.pageSize) || 1);
            const pageNumber = Math.min(prev.pageNumber, maxPage);
            return pageNumber === prev.pageNumber ? prev : { ...prev, pageNumber };
        });
    }, [total, paginationData.pageSize]);

    const tableData = useMemo(() => ({ rows, total, isLoading }), [rows, total, isLoading]);

    // public callbacks
    const setActiveSystem = useCallback((systemId) => {
        setPaginationData((p) => ({ ...p, pageNumber: 1 }));
        setRemoteSystemId(systemId);
    }, []);

    const handleInvalidateKey = useCallback(
        async (keyId) => invalidateKeyMutation.mutateAsync({ keyId }),
        [invalidateKeyMutation]
    );
    const handleActivateKey = useCallback(
        async (keyId) => activateKeyMutation.mutateAsync({ keyId }),
        [activateKeyMutation]
    );
    const handleExtendKey = useCallback(
        async (keyId) => extendKeyMutation.mutateAsync({ keyId }),
        [extendKeyMutation]
    );
    const handleGenerateKey = useCallback(
        async (systemId) => generateKeyMutation.mutateAsync({ remoteSystemId: systemId }),
        [generateKeyMutation]
    );

    // wire up table config
    const sortingCallback = useCallback((data) => {
        // data like { field: 'createdAt', direction: 'desc' }
        setLocalFilters((prev) => ({
            ...(prev || ApiKeysFilters),
            sorting: { ...(prev?.sorting || ApiKeysFilters.sorting || {}), ...data },
            pagination: { ...(ApiKeysFilters.pagination || {}), ...prev.pagination },
        }));
        setPaginationData((p) => ({ ...p, pageNumber: 1 }));
    }, []);

    const paginationCallback = useCallback(
        (newPaginationData) => setPaginationData((prev) => ({ ...prev, ...newPaginationData })),
        []
    );

    const config = useMemo(
        () => ({
            columns: ApiKeysColumns,
            filters: {
                ...localFilters,
                sorting: { ...localFilters.sorting, callback: sortingCallback },
                pagination: {
                    ...localFilters.pagination,
                    paginationEvent: paginationCallback,
                    paginationData,
                },
            },
            actions: ApiKeysActions,
        }),
        [ApiKeysColumns, localFilters, sortingCallback, paginationCallback, paginationData]
    );

    return html`
    <${ApiKeysTableContext.Provider}
      value=${{
          // actions
          handleActivateKey,
          handleInvalidateKey,
          handleGenerateKey,
          handleExtendKey,
          // selection
          setActiveSystem,
          // state
          fetchCacheKey,
          isSubmitting,
          isLoading,
          tableData,
          config,
      }}
    >
      ${children}
    </${ApiKeysTableContext.Provider}>`;
}

export const useApiKeysContext = () => useContext(ApiKeysTableContext);
